// src/pages/Connector/OneDrive/EditOneDriveFileContent.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import backendURL from "../../../config"; // Assuming this points to your backend base URL
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Button, Modal, Input, Space, Select, Dropdown, Menu } from 'antd'; // Ant Design components
import { DownOutlined } from '@ant-design/icons';

const { Option } = Select;

// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

export default function EditOneDriveFileContent({ document, onClose, onUpdate, isLoading: parentIsLoading }) {
  // Initialize pageContent directly from document if available, otherwise empty paragraph
  const [pageContent, setPageContent] = useState(document?.page_content || "<p></p>"); 
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState('save');
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      Table.configure({ resizable: true }),
      TableCell, TableHeader, TableRow,
      Link.configure({
        openOnClick: false, autolink: true, linkOnPaste: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
    ],
    // content is set via useEffect to ensure it updates when the document prop changes after mount
    content: '', // Initialize editor with empty and let useEffect populate
    onUpdate: ({ editor }) => {
      setPageContent(editor.getHTML()); // Update internal state on editor change
    },
    editorProps: {
      attributes: { class: 'tiptap-editor-area' },
    },
  });

  useEffect(() => {
    // This effect runs when the component mounts and when `document` or `editor` changes.
    // It's crucial for setting the initial content and updating if the document prop changes.
    if (editor && document) {
      const currentEditorContent = editor.getHTML();
      const newContent = document.page_content || "<p></p>"; // Default to empty paragraph if no content

      // Only update if the content is actually different to avoid unnecessary re-renders/cursor jumps
      if (currentEditorContent !== newContent) {
        editor.commands.setContent(newContent, false); // `false` to prevent emitting an update event
      }
      // Also update the local pageContent state if it's out of sync with the document
      // This normally shouldn't be necessary if onUpdate works correctly, but as a safeguard:
      if (pageContent !== newContent) {
          setPageContent(newContent);
      }
    } else if (editor && !document) {
      // If document becomes null/undefined, clear the editor
      if (editor.getHTML() !== "<p></p>") {
        editor.commands.setContent("<p></p>", false);
      }
      if (pageContent !== "<p></p>") {
          setPageContent("<p></p>");
      }
    }
  }, [document, editor]); // Add pageContent to dependencies if direct setPageContent above is used.
                        // However, editor.commands.setContent should trigger onUpdate, which updates pageContent.
                        // For safety, let's stick to document and editor for now.

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);

  const handleSave = async () => {
    setIsSaving(true);
    setToolbarSaveStatus('saving');
    setError(null);

    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      // Use the editor's current HTML content for saving
      const contentToSave = editor ? editor.getHTML() : pageContent;

      const payload = { 
        id: document.id, 
        page_content: contentToSave, 
        all_data: document 
      };

      const response = await axios.post(
        `${backendURL}/api/onedrive/update-file-content/${document.id}`,
        payload,
        { headers }
      );

      if (response.status === 200) {
        onUpdate(); 
        setToolbarSaveStatus('saved');
        setTimeout(() => setToolbarSaveStatus('save'), 1500);
      } else {
         setError(`Failed to update data (Status: ${response.status})`);
         setToolbarSaveStatus('save');
      }
    } catch (err) {
      const message = err.response?.data?.message || "Failed to update data.";
      setError(message);
      setToolbarSaveStatus('save');
      console.error("Error updating OneDrive file data:", err);
    } finally {
      setIsSaving(false);
    }
  };

  // --- Tiptap Toolbar Handlers (Copied from EditTrainedData.js, should be fine) ---
  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const handleInsertTable = useCallback(() => {
    if (!editor) return;
    const rows = parseInt(tableRows, 10);
    const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) {
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns]);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);

  const handleHeadingChange = useCallback((value) => {
    if (!editor) return;
    if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
    } else {
        const level = parseInt(value.slice(1), 10);
        if (level >= 1 && level <= 6) {
           editor.chain().focus().toggleHeading({ level }).run();
        }
    }
  }, [editor]);

  const handleAddColumn = useCallback(() => editor?.chain().focus().addColumnAfter().run(), [editor]);
  const handleAddRow = useCallback(() => editor?.chain().focus().addRowAfter().run(), [editor]);
  const handleDeleteColumn = useCallback(() => editor?.chain().focus().deleteColumn().run(), [editor]);
  const handleDeleteRow = useCallback(() => editor?.chain().focus().deleteRow().run(), [editor]);
  const handleDeleteTable = useCallback(() => editor?.chain().focus().deleteTable().run(), [editor]);

  if (!editor) {
    return <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-2x"></i> Loading Editor...</div>;
  }

  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={handleAddColumn} disabled={!editor.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={handleAddRow} disabled={!editor.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={handleDeleteColumn} disabled={!editor.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={handleDeleteRow} disabled={!editor.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={handleDeleteTable} disabled={!editor.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );

  const currentHeadingLevel = () => {
    if (!editor) return 'paragraph'; // Guard against editor not being ready
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return `h${i}`;
    }
    return 'paragraph';
  };
  // --- End Tiptap Toolbar Handlers ---

  return (
    <div className="edit-trained-data" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ paddingBottom: '10px', borderBottom: '1px solid #eee', marginBottom: '10px', flexShrink: 0 }}>
        <h5 style={{ margin: 0 }}>Edit OneDrive File Content: {document?.filename}</h5>
      </div>

      {/* Toolbar */}
      <div className="sticky-toolbar" style={{ flexShrink: 0, marginBottom: '10px', border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: '#f8f9fa' }}>
        <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
          <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fa fa-bold"></i></button>
          <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fa fa-italic"></i></button>
          <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fa fa-underline"></i></button>
          <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''} title="Strike"><i className="fa fa-strikethrough"></i></button>
          <button onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Link"><i className="fa fa-link"></i></button>
          
          <Select value={currentHeadingLevel()} style={{ width: 120 }} onChange={handleHeadingChange} disabled={!editor}>
            <Option value="paragraph">Paragraph</Option>
            {[1, 2, 3, 4, 5, 6].map(level => <Option key={level} value={`h${level}`}>Heading {level}</Option>)}
          </Select>

          <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fa fa-list-ul"></i></button>
          <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Ordered List"><i className="fa fa-list-ol"></i></button>
          
          <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1600 }} disabled={!editor}>
            <Button style={{ marginLeft: '8px', marginRight: '8px' }}><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></Button>
          </Dropdown>

          <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor || !editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></button>
          <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor || !editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></button>
          
          <button onClick={handleSave} title="Save Changes" disabled={!editor}>
            {toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}
          </button>
        </div>
      </div>

      <div style={{ flexGrow: 1, overflow: 'hidden', border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
        {editor ? 
            <EditorContent editor={editor} style={{ height: '100%', overflowY: 'auto' }}/>
            : <p>Editor initializing...</p>
        }
      </div>

      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {error && <div className="alert alert-danger" style={{ margin: 0, padding: '0.5rem 1rem' }}>{error}</div>}
        </div>
        <div>
          <Button type="primary" onClick={handleSave} loading={isSaving || parentIsLoading === 'editSave'} icon={<i className="fa fa-save" style={{ marginRight: '4px' }}></i>} style={{ marginRight: '8px' }} disabled={!editor}>
            {isSaving || parentIsLoading === 'editSave' ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={onClose} disabled={isSaving || parentIsLoading === 'editSave'} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </Button>
        </div>
      </div>

      <Modal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1700} /* Increased zIndex */>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
          <Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
        </Space>
      </Modal>
      
      <style jsx global>{`
            /* Copied styles from EditTrainedData.js - these should be identical */
            .tiptap-editor-area {
                min-height: 200px; 
                max-height: calc(90vh - 280px); 
                overflow-y: auto;
                border: 1px solid #ced4da; 
                border-radius: 0.25rem;
                padding: 0.5rem; /* Added padding for content inside editor */
            }
            .tiptap-editor-area:focus-within,
            .tiptap-editor-area.ProseMirror-focused { 
                border-color: #86b7fe;
                outline: 0;
                box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
            }
            .tiptap-toolbar {
                flex-shrink: 0;
                z-index: 1; /* Ensure toolbar is above editor content, but modal is higher */
                background-color: #f8f9fa; 
            }
            .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
            .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
            .ProseMirror li { margin-bottom: 0.1rem; }
            .ProseMirror li p { margin-bottom: 0.1rem; }
            .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
            .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 { margin-top: 0.75rem; margin-bottom: 0.3rem; line-height: 1.3; font-weight: 500; }
            .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
            .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
            .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
            .tiptap-toolbar .ant-btn-sm { margin-bottom: 2px; margin-top: 2px; }
            .tiptap-toolbar .ant-select-sm { margin: 2px 8px 2px 8px !important; vertical-align: middle; }

            /* Responsive height for editor area */
            .tiptap-editor-area { height: 160px; }
            @media (min-width: 640px) { .tiptap-editor-area { height: 208px; } }
            @media (min-width: 768px) { .tiptap-editor-area { height: 256px; } }
            @media (min-width: 1024px) { .tiptap-editor-area { height: 288px; } }
            @media (min-width: 1280px) { .tiptap-editor-area { height: 320px; } }

            .ant-select-dropdown, .ant-dropdown-menu { z-index: 1650 !important; /* Higher than MUI modal base, lower than AntD modal */ }
            .toolbar button { margin-right: 4px; padding: 4px 8px; }
            .toolbar button.is-active { background-color: #e0e0e0; }
        `}</style>
    </div>
  );
}